import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar, Car, Clock, ChevronRight, Loader2, Star } from "lucide-react";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";

const STATUS_CLASS = {
  requested: "status-pending",
  confirmed: "status-confirmed",
  active: "status-active",
  completed: "status-completed",
  canceled: "status-canceled",
};

const STATUS_KEY = {
  requested: "trips.statusRequested",
  confirmed: "trips.statusConfirmed",
  active: "trips.statusActive",
  completed: "trips.statusCompleted",
  canceled: "trips.statusCanceled",
};

export default function TripsPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? enUS : es;
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [reviewMap, setReviewMap] = useState({}); // booking_id -> reviewed?

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await api.get("/bookings?role=guest");
      const list = response.data || [];
      setBookings(list);
      // Check which completed bookings still need a review
      const completed = list.filter((b) => b.status === "completed");
      const map = {};
      await Promise.all(
        completed.map(async (b) => {
          try {
            const r = await api.get(`/reviews/booking/${b.booking_id}`);
            map[b.booking_id] = !!r.data?.reviewed;
          } catch {
            map[b.booking_id] = false;
          }
        })
      );
      setReviewMap(map);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = (tab) => {
    return bookings.filter((b) => {
      if (tab === "upcoming") {
        return ["requested", "confirmed"].includes(b.status);
      } else if (tab === "active") {
        return b.status === "active";
      } else {
        return ["completed", "canceled"].includes(b.status);
      }
    });
  };

  const BookingCard = ({ booking }) => {
    const statusClass = STATUS_CLASS[booking.status] || STATUS_CLASS.requested;
    const statusLabel = t(STATUS_KEY[booking.status] || STATUS_KEY.requested);
    const pickupDate = new Date(booking.pickup_date);
    const returnDate = new Date(booking.return_date);
    const dayCount = Math.ceil((returnDate - pickupDate) / (1000 * 60 * 60 * 24));

    return (
      <Link
        to={`/trips/${booking.booking_id}`}
        className="card-base p-4 hover-lift block"
        data-testid={`trip-card-${booking.booking_id}`}
      >
        <div className="flex gap-4">
          <img
            src={booking.vehicle?.photos?.[0] || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=200"}
            alt={`${booking.vehicle?.make} ${booking.vehicle?.model}`}
            className="w-24 h-24 object-cover rounded-xl flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-heading font-semibold text-lg truncate">
                {booking.vehicle?.make} {booking.vehicle?.model} {booking.vehicle?.year}
              </h3>
              <Badge className={statusClass}>{statusLabel}</Badge>
            </div>

            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>
                  {format(pickupDate, "dd MMM", { locale: dateLocale })} -{" "}
                  {format(returnDate, "dd MMM yyyy", { locale: dateLocale })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{t("trips.days", { count: dayCount })}</span>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3">
              <span className="font-semibold text-terracotta">
                ${booking.total_price?.toLocaleString()} MXN
              </span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="trips-page">
      <div className="container-main py-8">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave mb-6">
          {t("trips.title")}
        </h1>

        {/* Pending review reminder */}
        {(() => {
          const pending = bookings.filter(
            (b) => b.status === "completed" && reviewMap[b.booking_id] === false
          );
          if (!pending.length) return null;
          return (
            <div
              className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3"
              data-testid="review-reminder-banner"
            >
              <Star className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-amber-900">
                  {t("trips.pendingReview", { count: pending.length })}
                </p>
                <p className="text-amber-700">{t("trips.reviewHelp")}</p>
              </div>
              <Link to={`/trips/${pending[0].booking_id}`}>
                <Button size="sm" className="bg-[#111111] hover:bg-neutral-800 text-white">
                  {t("trips.rateNow")}
                </Button>
              </Link>
            </div>
          );
        })()}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="upcoming">{t("trips.upcoming")}</TabsTrigger>
            <TabsTrigger value="active">{t("trips.active")}</TabsTrigger>
            <TabsTrigger value="past">{t("trips.past")}</TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
            </div>
          ) : (
            <>
              <TabsContent value="upcoming" className="space-y-4">
                {filterBookings("upcoming").length > 0 ? (
                  filterBookings("upcoming").map((booking) => (
                    <BookingCard key={booking.booking_id} booking={booking} />
                  ))
                ) : (
                  <EmptyState
                    title={t("trips.emptyUpcomingTitle")}
                    description={t("trips.emptyUpcomingBody")}
                  />
                )}
              </TabsContent>

              <TabsContent value="active" className="space-y-4">
                {filterBookings("active").length > 0 ? (
                  filterBookings("active").map((booking) => (
                    <BookingCard key={booking.booking_id} booking={booking} />
                  ))
                ) : (
                  <EmptyState
                    title={t("trips.emptyActiveTitle")}
                    description={t("trips.emptyActiveBody")}
                  />
                )}
              </TabsContent>

              <TabsContent value="past" className="space-y-4">
                {filterBookings("past").length > 0 ? (
                  filterBookings("past").map((booking) => (
                    <BookingCard key={booking.booking_id} booking={booking} />
                  ))
                ) : (
                  <EmptyState
                    title={t("trips.emptyPastTitle")}
                    description={t("trips.emptyPastBody")}
                  />
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
}

function EmptyState({ title, description }) {
  const { t } = useTranslation();
  return (
    <div className="text-center py-16">
      <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-gray-500 mb-6">{description}</p>
      <Link to="/search">
        <Button className="btn-primary">{t("trips.searchCars")}</Button>
      </Link>
    </div>
  );
}
